<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  LeaveReason,
  getLeaveReasons,
  stateLeaveReason
} from '@/app/core/services/leave-reasons'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const leaveReasonsUpdateState = ref()

const loading = ref(false)

/* Datos */
const search = ref()
const tableLeaveReason = ref(null)

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('leaveReasons.list.code'), table: 'code' },
  { name: 'name', title: t('leaveReasons.list.name'), table: 'name' },
  { name: 'listMasterConcepts', title: t('leaveReasons.list.concepts') },
  { name: 'state', title: t('leaveReasons.list.state'), table: 'is_inactive' },
  {
    name: 'updated_at',
    title: t('leaveReasons.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<LeaveReason[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createLeaveReason = () => {
  void router.push({ name: 'CreateLeaveReason' })
}
const updateLeaveReason = (id: any) => {
  void router.push({ name: 'UpdateLeaveReason', params: { id } })
}
const getLeaveReason = (id: any) => {
  void router.push({ name: 'GetLeaveReason', params: { id } })
}

/* Cambiar estado de motivo de retiro */
const changeStateLeaveReason = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  leaveReasonsUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  showModal.value = true
  titleModal.value = t('leaveReasons.list.actionStatus', {
    status: text,
    leaveReason: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
}
const changeStateConfirm = async () => {
  showModal.value = false
  await stateLeaveReason(
    leaveReasonsUpdateState.value[0].id,
    leaveReasonsUpdateState.value[0].is_inactive as boolean
  )
    .then(response => {
      notifications.addNotification({
        title: !leaveReasonsUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('leaveReasons.notification.changeStatus', {
          leaveReason: leaveReasonsUpdateState.value[0].name as string,
          status: !leaveReasonsUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void leaveReasons()
    })
    .catch(async (error: any) => {
      notificationError(error, 'leaveReasons', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar Motivos de retiro */
const leaveReasons = async () => {
  loading.value = true
  try {
    const data: any = await getLeaveReasons(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'leaveReasons', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="changeStateConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('leaveReasons.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('leaveReasons.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="leaveReasons" />
          <DBtn
            @click.prevent="leaveReasons"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          v-if="store.hasPermission('leave_reason:create')"
          :icon="'PlusIcon'"
          type="button"
          @click="createLeaveReason()">
          {{
            t('form.button.create', { form: t('leaveReasons.list.title', 0) })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableLeaveReason"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), leaveReasons()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), leaveReasons()">
      <template #listMasterConcepts="data: any">
        <div class="flex items-center">
          <div
            v-for="(concepts, index) in data.masterConcepts
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ concepts }}
          </div>
          <div
            v-if="data.masterConcepts.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.masterConcepts.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('leave_reason:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getLeaveReason(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive && store.hasPermission('leave_reason:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateLeaveReason(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('leave_reason:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('leaveReasons.list.activate')
                : t('leaveReasons.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateLeaveReason(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
